import React, { useEffect, useState } from "react";
import Graph from "react-vis-graph-wrapper";
import { Box } from "@mui/material";
import { getColor, logging } from "../../tools";

const BLACK_COLOR = "#000000";
const LIGHT_COLOR = "#f0eded";
const GREY_COLOR = "#a8a5a5";
const NODE_SIZE = 20;

function getEdgeWidth(val) {
  if (val >= 0.7) return 7;
  if (val <= 0.1) return 1;
  return val * 10;
}

const initGraph = { nodes: [], edges: [] };

// docs: https://visjs.github.io/vis-network/docs/network/
export default function NetworkDiagram({ settings, result }) {
  const [tempGraph, setTempGraph] = useState(initGraph);
  const [graph, setGraph] = useState(initGraph);
  useEffect(() => {
    logging("Result");
    const vector = Object.values(result).flat();
    logging(vector);
    let edges = [];
    for (
      let index = 0;
      index < settings.labels.length * settings.labels.length;
      index++
    ) {
      const value = vector[index];
      const from = Math.floor(index / settings.labels.length);
      const to = index % settings.labels.length;
      let edge = {
        from: from,
        to: to,
        width: from === to ? 0 : getEdgeWidth(Math.abs(value)),
        dashes: value < 0 ? [16] : false,
        color: from === to ? "#fff" : BLACK_COLOR,
        props: { value: value, from: from, to: to },
        //label: Math.random(100),
        /* color:
            value > 0
              ? `rgba(103, 158, 49, ${value})`
              : `rgba(201, 31, 28, ${Math.abs(value)})`, */
      };
      const inList =
        edges.filter((e) => e.from === edge.to && e.to === edge.from).length >
        0;
      if (
        //edge.from !== edge.to && // don't show autocorrelations
        !inList && // no double lines between connections
        value !== 0 // no important values are set to 0
      ) {
        // add to edges
        edges.push(edge);
      }
    }

    // MAKE THE NODES
    // only show the four stong PCC connections in color
    const nodes = settings.labels.map((label, index) => {
      const isIsolated =
        edges.filter((edge) => edge.from === index || edge.to === index)
          .length === 0; // needed for node color
      const color = isIsolated
        ? LIGHT_COLOR
        : settings?.colors?.[index]
        ? settings.colors[index]
        : getColor(settings.labels[index]);
      const autoCorrValue = vector[index * settings.labels.length + index];
      //const borderColor = autoCorrValue !== 0 ? "#000" : color;
      //const borderWidth = autoCorrValue !== 0 ? autoCorrValue * 7 : 1; // variërende dikte van zwarte lijnen rondom de nodes waar autocorrelatie is gevonden
      const borderColor = color;
      const borderWidth = 1;
      const icon = autoCorrValue !== 0 ? { code: "↺", color: "#fff" } : {};
      let node = {
        id: index,
        label: label,
        color: {
          background: color,
          border: borderColor,
          hover: { background: color, border: borderColor },
          highlight: { background: color, border: borderColor },
        }, // only show connected nodes in color, other nodes  in grey
        borderWidth: borderWidth,
        borderWidthSelected: borderWidth,
        size: NODE_SIZE,
        x: settings.x[index],
        y: settings.y[index],
        icon: icon, // ➥ ⟲ ↺ ⬤
        scaling: { min: 20, max: 20 },
      };
      return node;
    });

    edges
      .filter((edge) => edge.from !== edge.to) // do not include autocorrelations
      .sort((a, b) => Math.abs(b.props.value) - Math.abs(a.props.value)) // sort the connection on PCC value
      .forEach((edge, index) => {
        if (index < 4) {
          // show the tooltip for 4 strongest correlations
          const value = edge.props.value;
          const fromLabel = settings.labels[edge.props.from];
          const toLabel = settings.labels[edge.props.to];
          edge.title = `Meer ${fromLabel} hangt samen met ${
            value > 0 ? "meer" : "minder"
          } ${toLabel}`;
        } else {
          edge.color = GREY_COLOR; // make the connection color grey
        }
      });
    // set the graph
    setGraph({ nodes, edges });
    logging({ nodes, edges });
    setTempGraph({ nodes, edges }); // store a copy (for hovering)
  }, [result, settings]);

  const options = {
    interaction: {
      hover: true,
      hoverConnectedEdges: false,
      zoomView: false, // user cannot zoom
    },
    layout: {
      hierarchical: false,
    },
    edges: {
      color: BLACK_COLOR,
      arrows: {
        to: { enabled: false },
        middle: { enabled: false },
        from: { enabled: false },
      },
      selectionWidth: 0,
      hoverWidth: 0,
    },
    height: "500px",
    nodes: {
      shape: "dot",
      /*  chosen: {
        node: (values, id, selected, hovering) => {
          console.log({ values, id, selected, hovering });
        },
      }, */
    },
    physics: {
      enabled: false,
    },
  };
  const hoverNode = (node) => {
    const highLightedConnections = [...tempGraph.edges].filter(
      (edge) => edge.from === node.node || edge.to === node.node
    );
    let highLightedNodes = [node.node]; // remember the highlighted nodes numbers
    highLightedConnections.forEach(({ from, to }) => {
      if (!highLightedNodes.includes(from)) highLightedNodes.push(from);
      if (!highLightedNodes.includes(to)) highLightedNodes.push(to);
    });
    // keep the color of the highlighted nodes and make the color of other nodes grey
    const nodes = [...tempGraph.nodes].map(({ id, label, color, size }) => ({
      id,
      label,
      color: highLightedNodes.includes(id) ? color : LIGHT_COLOR,
      size: id === node.node ? size * 1.15 : size, // make the node bigger on hovering
    }));
    const edges = [...tempGraph.edges].map(
      (edge) =>
        edge.from === node.node || edge.to === node.node
          ? { ...edge, color: BLACK_COLOR } // show a black line between highlighted connections
          : { ...edge, color: LIGHT_COLOR } //
    );
    setGraph({ nodes, edges });
  };
  const blurNode = () => {
    const nodes = [...tempGraph.nodes].map(({ id, label, color, size }) => ({
      id,
      label,
      color,
      size,
    }));
    setGraph({ nodes, edges: tempGraph.edges });
  };
  return (
    <Box>
      <Graph
        events={
          {
            //hoverNode: hoverNode,
            //blurNode: blurNode,
          }
        }
        graph={graph}
        options={options}
      />
    </Box>
  );
}
